import type { BrowserBackend, Item, ItemInput, Task, TaskInput, TaskPatch } from "./contracts";
import { LocalResourceClient } from "../platform/resource-client";
import type { LocalAppSession } from "../platform/kernel";

export class ApiClient {
  private readonly items: LocalResourceClient<Item, ItemInput, Partial<ItemInput>>;
  private readonly tasks: LocalResourceClient<Task, TaskInput, TaskPatch>;

  constructor(
    private readonly backend: BrowserBackend,
    itemApp?: LocalAppSession,
    taskApp?: LocalAppSession,
  ) {
    this.items = itemApp
      ? itemApp.resource<Item, ItemInput, Partial<ItemInput>>("items")
      : new LocalResourceClient<Item, ItemInput, Partial<ItemInput>>(backend, "/api/items");
    this.tasks = taskApp
      ? taskApp.resource<Task, TaskInput, TaskPatch>("tasks")
      : new LocalResourceClient<Task, TaskInput, TaskPatch>(backend, "/api/tasks");
  }

  async health(): Promise<Record<string, unknown>> {
    return this.json<Record<string, unknown>>(new Request(this.url("/api/health")));
  }

  async listItems(): Promise<Item[]> {
    return this.items.list();
  }

  async createItem(input: ItemInput): Promise<Item> {
    return this.items.create(input);
  }

  async updateItem(id: string, input: Partial<ItemInput>, version: number): Promise<Item> {
    return this.items.update(id, input, version);
  }

  async deleteItem(id: string, version: number): Promise<void> {
    await this.items.delete(id, version);
  }

  async listTasks(): Promise<Task[]> {
    return this.tasks.list();
  }

  async createTask(input: TaskInput): Promise<Task> {
    return this.tasks.create(input);
  }

  async updateTask(id: string, input: TaskPatch, version: number): Promise<Task> {
    return this.tasks.update(id, input, version);
  }

  async deleteTask(id: string, version: number): Promise<void> {
    await this.tasks.delete(id, version);
  }

  async request(request: Request): Promise<Response> {
    const response = await this.backend.request(request);
    if (!response.ok) {
      const payload = await response.clone().json().catch(() => undefined) as { error?: { code?: string; message?: string } } | undefined;
      const code = payload?.error?.code ?? `http_${response.status}`;
      const message = payload?.error?.message ?? `Request failed with ${response.status}.`;
      throw new Error(`${code}: ${message}`);
    }
    return response;
  }

  private async json<T>(request: Request): Promise<T> {
    const response = await this.request(request);
    return response.json() as Promise<T>;
  }

  private url(path: string): string {
    return new URL(path, window.location.origin).toString();
  }
}
